import { useTranslation } from 'react-i18next'
import type { PackagingFieldCrossCheckResult } from '@/shared/hooks/api/packagingSuppliers'
import { Badge } from '@/components/ui/badge'

export function formatRunTimestamp(timestamp: string, locale: string): string {
  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime())) {
    return timestamp
  }
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date)
}

type PackagingFieldCrossCheckRunResultsProps = {
  result: PackagingFieldCrossCheckResult
  title?: string
}

export function PackagingFieldCrossCheckRunResults({
  result,
  title,
}: PackagingFieldCrossCheckRunResultsProps) {
  const { t, i18n } = useTranslation('packagingSuppliers')
  const useChineseReasoning = i18n.language.startsWith('zh')

  return (
    <div className='space-y-4 rounded-md border p-4'>
      <div className='flex flex-wrap items-center gap-2'>
        {title ? <span className='text-sm font-medium'>{title}</span> : null}
        {!title ? (
          <span className='text-sm font-medium'>
            {t('itemDetail.audits.resultTitle')}
          </span>
        ) : null}
        {result.flagged ? (
          <Badge variant='destructive'>{t('fieldCrossCheck.flagged')}</Badge>
        ) : (
          <Badge variant='secondary'>{t('fieldCrossCheck.clear')}</Badge>
        )}
        <Badge variant='outline'>
          {t('fieldCrossCheck.confidence', { level: result.confidence })}
        </Badge>
      </div>

      <ul className='space-y-3'>
        {result.checks.map((check) => (
          <li
            key={check.code}
            className='space-y-2 rounded-md border p-3 text-sm'
          >
            <div className='flex flex-wrap items-center gap-2'>
              <p className='font-medium'>
                {t(`fieldCrossCheck.checks.${check.code}`)}
              </p>
              {check.passed ? (
                <Badge variant='success'>{t('fieldCrossCheck.passed')}</Badge>
              ) : (
                <Badge variant='danger'>{t('fieldCrossCheck.failed')}</Badge>
              )}
            </div>
            <p className='text-muted-foreground'>
              {useChineseReasoning ? check.reasoningZh : check.reasoningEn}
            </p>
            {!check.passed && check.fields.length > 0 && (
              <p className='text-muted-foreground'>
                {t('fieldCrossCheck.fields', {
                  fields: check.fields.join(', '),
                })}
              </p>
            )}
            {!check.passed && check.componentIndex != null && (
              <p className='text-muted-foreground'>
                {t('fieldCrossCheck.componentIndex', {
                  index: check.componentIndex,
                })}
              </p>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}
